import { useCallback, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ChevronDown, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import { usePostHog } from "@posthog/react";

// ─── Web3Forms access key ────────────────────────────────────────────────────
// Sourced from VITE_WEB3FORMS_ACCESS_KEY (set in .env.local, untracked).
// Web3Forms keys are designed for client-side use; harden by enabling domain
// restrictions in the Web3Forms dashboard. For stronger protection, proxy
// submissions through an edge function so the key never ships to the browser.
const WEB3FORMS_ACCESS_KEY =
  import.meta.env.VITE_WEB3FORMS_ACCESS_KEY ?? "8bbce4fb-d8cd-4ff5-a3c9-b459c41cd0b4";

interface FormState {
  name: string;
  email: string;
  company: string;
  question: string;
  botcheck: boolean; // honeypot: must remain false for real submissions
}

const INITIAL_STATE: FormState = {
  name: "",
  email: "",
  company: "",
  question: "",
  botcheck: false,
};

type Status = "idle" | "loading" | "success" | "error";

const ContactForm = () => {
  const t = useTranslation();
  const f = t.contactForm;
  const prefersReduced = useReducedMotion();
  const posthog = usePostHog();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(INITIAL_STATE);
  const [status, setStatus] = useState<Status>("idle");

  const toggleBtnRef = useRef<HTMLButtonElement>(null);

  const handleToggleMove = useCallback((e: React.MouseEvent) => {
    if (prefersReduced || !toggleBtnRef.current || !window.matchMedia("(pointer: fine)").matches) return;
    const rect = toggleBtnRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = Math.max(-8, Math.min(8, (e.clientX - cx) * 0.15));
    const dy = Math.max(-8, Math.min(8, (e.clientY - cy) * 0.15));
    toggleBtnRef.current.style.transform = `translate(${dx}px, ${dy}px)`;
  }, [prefersReduced]);

  const handleToggleLeave = useCallback(() => {
    if (toggleBtnRef.current) toggleBtnRef.current.style.transform = "translate(0, 0)";
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check: bots fill hidden fields, humans don't
    if (form.botcheck) return;

    setStatus("loading");
    posthog?.capture("contact_form_submitted", {
      has_company: !!form.company,
    });

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          // Future CRM/Notion hook: add webhook_url: "https://your-webhook.url" here
          subject: `New question from ${form.name}`,
          from_name: form.name,
          email: form.email,
          "First name": form.name,
          "Company / project": form.company || "not provided",
          "Question": form.question,
          botcheck: form.botcheck,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus("success");
        posthog?.identify(form.email, { name: form.name, email: form.email, company: form.company || undefined });
        setForm(INITIAL_STATE);
      } else {
        setStatus("error");
        posthog?.capture("contact_form_error", { reason: "api_error" });
      }
    } catch (err) {
      setStatus("error");
      posthog?.capture("contact_form_error", { reason: "network_error" });
      posthog?.captureException(err);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Toggle button — big magnetic CTA (mouse-follow on fine pointers) */}
      <div
        onMouseMove={handleToggleMove}
        onMouseLeave={handleToggleLeave}
        className="flex justify-center"
      >
        <button
          ref={toggleBtnRef}
          onClick={() => {
            const next = !open;
            setOpen(next);
            if (next) posthog?.capture("contact_form_opened");
          }}
          className="group inline-flex items-center gap-2 px-7 py-3.5 min-h-[44px] rounded-full bg-primary text-primary-foreground font-body font-semibold text-sm hover:bg-primary/90 transition-all glow"
          style={{ transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms" }}
        >
          {f.toggleLabel}
          <ChevronDown
            size={14}
            className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* Form drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="mt-6 rounded-xl border border-border bg-card/60 p-6 sm:p-8">
              <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-1">
                {f.heading}
              </p>
              <p className="text-sm font-body text-muted-foreground mb-6">{f.subheading}</p>

              {status === "success" ? (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-3 p-4 rounded-lg border border-primary/30 bg-primary/5"
                >
                  <CheckCircle2 size={18} className="text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-sm font-body text-foreground">{f.success}</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Honeypot: hidden from real users, filled by bots */}
                  <input
                    type="checkbox"
                    name="botcheck"
                    checked={form.botcheck}
                    onChange={handleChange}
                    className="hidden"
                    tabIndex={-1}
                    aria-hidden="true"
                  />

                  {/* Name + Email row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-body font-medium text-foreground mb-2">
                        {f.fields.name.label}
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder={f.fields.name.placeholder}
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-border/60 bg-background text-sm font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-body font-medium text-foreground mb-2">
                        {f.fields.email.label}
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder={f.fields.email.placeholder}
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-border/60 bg-background text-sm font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                      />
                    </div>
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-xs font-body font-medium text-foreground mb-2">
                      {f.fields.company.label}
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      placeholder={f.fields.company.placeholder}
                      className="w-full px-4 py-2.5 rounded-lg border border-border/60 bg-background text-sm font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                    />
                  </div>

                  {/* Question */}
                  <div>
                    <label className="block text-xs font-body font-medium text-foreground mb-2">
                      {f.fields.question.label}
                    </label>
                    <textarea
                      name="question"
                      value={form.question}
                      onChange={handleChange}
                      placeholder={f.fields.question.placeholder}
                      maxLength={f.fields.question.maxLength}
                      rows={4}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-border/60 bg-background text-sm font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors resize-none"
                    />
                    <p className="text-xs font-body text-muted-foreground mt-1 text-right">
                      {form.question.length} / {f.fields.question.maxLength}
                    </p>
                  </div>

                  {/* Error message */}
                  {status === "error" && (
                    <motion.div
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-start gap-3 p-4 rounded-lg border border-red-500/20 bg-red-500/5"
                    >
                      <AlertCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm font-body text-red-400">{f.error}</p>
                    </motion.div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-body font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "loading" ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        {f.sending}
                      </>
                    ) : (
                      f.submit
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContactForm;
